import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTw } from '../../../../../utils/Styles'
import { getHighestStatus } from '../../../../../utils'
import { StatusPageQuery } from '../StatusPage.generated'
import { StatusPageEventHistoryItemComponentStatus as Status } from '../../../../../api/types'

type Props = {
  summary: StatusPageQuery['Profile']['statusPage']['summary']
  components: StatusPageQuery['Profile']['statusPage']['components']
}

function Ring({ status }: { status: Status }) {
  const tw = useTw()
  return (
    <div className={tw`tw`}>
      <div
        className={tw`
          rounded-full ring-8 ring-opacity-30 h-8 w-8
          ${status === Status.Operational && 'ring-green-500 bg-green-500'}
          ${status === Status.Degraded && 'ring-yellow-400 bg-yellow-400'}
          ${status === Status.PartialOutage && 'ring-yellow-600 bg-yellow-600'}
          ${status === Status.MajorOutage && 'ring-red-500 bg-red-500'}
        `}
      ></div>
    </div>
  )
}

function Item({ percentage, text }: { percentage: string; text: string }) {
  const tw = useTw()
  return (
    <div className={tw`px-2`}>
      <p className={tw`text-2xl font-medium`}>{percentage}%</p>
      <p className={tw`text-gray-500 text-sm`}>{text}</p>
    </div>
  )
}

export function StatusPageSummary({ summary, components }: Props) {
  const tw = useTw()
  const { t } = useTranslation('common')

  const issues = useMemo(
    () =>
      components
        .map((_) => _.eventsHistory[0].events)
        .flat()
        .filter((_) => _.ongoing)
        .map((_) => _?.historyItems?.[0].affectedComponents ?? [])
        .flat()
        .map((_) => _.componentStatus),
    [components]
  )

  const status = getHighestStatus(issues)

  return (
    <div className={tw`flex flex-col w-full py-8 px-6`}>
      <div className={tw`flex space-x-6 items-center flex-grow pb-4`}>
        <Ring status={status} />
        <p className={tw`text-lg leading-6 font-medium`}>
          {t(`status_page.statuses.${status}`)}
        </p>
      </div>
      <div className={tw`grid grid-cols-3 divide-x -ml-2 -mr-2 text-center`}>
        <Item
          text={t('status_page.last_days.24h')}
          percentage={summary.last24h.toFixed(2)}
        />
        <Item
          text={t('status_page.last_days.7d')}
          percentage={summary.last7d.toFixed(2)}
        />
        <Item
          text={t('status_page.last_days.90d')}
          percentage={summary.last90d.toFixed(2)}
        />
      </div>
    </div>
  )
}
